/**
 * SvgRenderer — imperative renderer that owns an <svg> and paints a Scene into
 * it. React only provides the mount container; this class manages the SVG DOM
 * directly (like Excalidraw manages its canvas outside React) for full control
 * over the camera transform, animated overlays, and annotation layers.
 *
 * Layer stack (bottom -> top), all inside the camera-transformed world group
 * except the screen background/overlay:
 *   bg (screen)  <  grid  <  groups  <  edges  <  nodes  <  annotations  <  screen-overlay
 */

import rough from "roughjs";
import type { Point } from "../geometry.js";
import type { Scene, SceneNode } from "../scene/types.js";
import { registerBuiltinShapes } from "../plugins/builtins.js";
import { ensurePluginStyles } from "../plugins/registry.js";
import { AnnotationLayer } from "../annotate/layer.js";
import { renderEdge } from "./edges.js";
import { renderCharacterNode, renderIconNode } from "./figures.js";
import { applyStrokeTuning, labelBelow, renderShapeBody, sceneRoughOptions, type RoughRenderTuning } from "./shapes.js";
import { ensureEngineStyles, FONT_FAMILY } from "./theme.css.js";

const SVG_NS = "http://www.w3.org/2000/svg";

/** Reveal effect name (from the DSL `reveal … with <effect>`) -> CSS class. */
const REVEAL_EFFECT_CLASS: Record<string, string | undefined> = {
  fade: "edd-reveal-fade",
  pop: "edd-reveal-pop",
  sweep: "edd-reveal-sweep",
  // `draw-on` is a real stroke-by-stroke drawing — only a frame-driven host can
  // play it (setRevealProgressAll). The interactive player has no wall-clock
  // stroke animation, so it degrades to the sweep wipe (its behaviour before
  // "draw-on" became a distinct revealFx value).
  "draw-on": "edd-reveal-sweep",
};

export interface CameraTransform {
  cx: number;
  cy: number;
  zoom: number;
}

export interface SvgRendererOptions {
  /**
   * Deterministic frame-by-frame rendering for screenshot/video consumers
   * (Remotion, Puppeteer, export pipelines): disables ALL wall-clock CSS —
   * visibility transitions, animated-arrow keyframes (the overlay isn't even
   * emitted), and reveal animations — so any captured frame is final, never
   * mid-transition. Drive motion explicitly via applyVisibility/applyCamera/
   * setRevealProgress instead.
   */
  static?: boolean;
  /**
   * Paint the scene's ALWAYS-ON annotations (`scene.annotations`, i.e. the
   * top-level `annotate { … }` block) as part of `render()`. Default true —
   * without it a plain `renderer.render(scene)` silently drops every mark and
   * the caller has to know to build an `AnnotationLayer` itself. Set false
   * only when something else owns the annotations layer (TimelinePlayer and
   * the `EdodoDraw` facade both re-render it right after, so they're
   * unaffected either way).
   */
  annotations?: boolean;
  /**
   * Stamp `vector-effect="non-scaling-stroke"` on every generated drawable, so
   * a 2px line stays 2px at any camera zoom instead of becoming 8px at 4x.
   * Default false (an interactive canvas wants strokes to zoom like the
   * drawing); video/punch-in hosts want it on. Pairs with
   * `setRoughnessScale()`: one holds stroke WIDTH constant on screen, the
   * other holds stroke JITTER constant.
   */
  nonScalingStroke?: boolean;
  /**
   * Initial roughness scale — see `setRoughnessScale()`. Default 1.
   */
  roughnessScale?: number;
  /**
   * Initial stroke-width multiplier — see `setStrokeScale()`. Default 1.
   */
  strokeScale?: number;
}

export class SvgRenderer {
  readonly container: HTMLElement;
  /** True when constructed with `{ static: true }` — see SvgRendererOptions. */
  readonly isStatic: boolean;
  /** See SvgRendererOptions.annotations. */
  readonly paintsAnnotations: boolean;
  /** See SvgRendererOptions.nonScalingStroke. */
  readonly nonScalingStroke: boolean;
  svg!: SVGSVGElement;
  private defs!: SVGDefsElement;
  private bg!: SVGRectElement;
  world!: SVGGElement;
  private layers!: Record<string, SVGGElement>;
  screenLayer!: SVGGElement;
  private rc!: ReturnType<(typeof rough)["svg"]>;

  private camera: CameraTransform = { cx: 0, cy: 0, zoom: 1 };
  private viewport = { w: 800, h: 600 };
  private scene: Scene | null = null;
  private roughnessScale = 1;
  private strokeScale = 1;

  constructor(container: HTMLElement, options: SvgRendererOptions = {}) {
    this.container = container;
    this.isStatic = options.static ?? false;
    this.paintsAnnotations = options.annotations ?? true;
    this.nonScalingStroke = options.nonScalingStroke ?? false;
    this.roughnessScale = clampScale(options.roughnessScale ?? 1);
    this.strokeScale = clampScale(options.strokeScale ?? 1);
  }

  /** The render-time rough knobs, as every draw call wants them. */
  private roughTune(): RoughRenderTuning {
    return { roughnessScale: this.roughnessScale, nonScalingStroke: this.nonScalingStroke, strokeScale: this.strokeScale };
  }

  /** Current roughness scale — see `setRoughnessScale()`. */
  getRoughnessScale(): number {
    return this.roughnessScale;
  }

  /**
   * Scale every `roughness` and `maxRandomnessOffset` by `k` and repaint.
   *
   * rough.js perturbs geometry in WORLD units and the camera is a
   * `scale(zoom)` on the world group, so on-screen jitter is `zoom x world
   * jitter`: a punch-in magnifies the scratchiness along with the drawing. A
   * frame-driven host cancels that by passing `k = 1/zoom`.
   *
   * Regenerating strokes is a full re-render (single-digit ms for a normal
   * diagram, but not free), so QUANTISE the zoom — e.g.
   * `k = 1 / 2 ** Math.round(Math.log2(zoom))` — and this will regenerate a
   * handful of times per composition instead of once per frame. Calling it
   * with the value it already has is a no-op, so quantised callers are safe to
   * call it every frame.
   *
   * Seeds are untouched, so the strokes stay deterministic: the same scene at
   * the same scale always draws identically.
   */
  setRoughnessScale(k: number): void {
    const next = clampScale(k);
    if (next === this.roughnessScale) return;
    this.roughnessScale = next;
    if (this.scene) this.render(this.scene);
  }

  /**
   * Apply this renderer's stroke policy to a subtree someone else painted into
   * one of its layers (the annotation layer does exactly that). No-op unless
   * `nonScalingStroke` is on or the stroke scale is not 1.
   */
  applyStrokePolicy(root: Element): void {
    applyStrokeTuning(root, this.roughTune());
  }

  /** Current stroke-width multiplier — see `setStrokeScale()`. */
  getStrokeScale(): number {
    return this.strokeScale;
  }

  /**
   * Multiply every drawn stroke width by `k` and repaint — node outlines,
   * edges, arrowheads, hachure, icons, group frames, annotation marks. Lets a
   * video host set line weight from its own theme (under `nonScalingStroke`,
   * a preset's 1.8px is 1.8 SCREEN px, thin on a 1920x1080 frame) without
   * restyling the diagram. Geometry and seeds are untouched; calling it with
   * the value it already has is a no-op.
   */
  setStrokeScale(k: number): void {
    const next = clampScale(k);
    if (next === this.strokeScale) return;
    this.strokeScale = next;
    if (this.scene) this.render(this.scene);
  }

  mount(): void {
    registerBuiltinShapes(); // ensure built-in plugin shapes survive lib builds
    ensureEngineStyles(this.container.ownerDocument);
    const doc = this.container.ownerDocument;
    const svg = doc.createElementNS(SVG_NS, "svg") as SVGSVGElement;
    svg.setAttribute("class", this.isStatic ? "edd-canvas edd-static" : "edd-canvas");
    svg.style.position = "absolute";
    svg.style.inset = "0";
    svg.style.width = "100%";
    svg.style.height = "100%";
    svg.style.display = "block";
    svg.style.userSelect = "none";
    svg.style.touchAction = "none";
    this.svg = svg;

    this.defs = doc.createElementNS(SVG_NS, "defs") as SVGDefsElement;
    this.defs.innerHTML = this.defsMarkup();
    svg.appendChild(this.defs);

    this.bg = doc.createElementNS(SVG_NS, "rect") as SVGRectElement;
    this.bg.setAttribute("class", "edd-bg");
    this.bg.setAttribute("x", "0");
    this.bg.setAttribute("y", "0");
    this.bg.setAttribute("width", "100%");
    this.bg.setAttribute("height", "100%");
    this.bg.setAttribute("fill", "#ffffff");
    svg.appendChild(this.bg);

    this.world = doc.createElementNS(SVG_NS, "g") as SVGGElement;
    this.world.setAttribute("class", "edd-world");
    svg.appendChild(this.world);

    this.layers = {};
    for (const name of ["grid", "groups", "edges", "nodes", "annotations", "live"]) {
      const g = doc.createElementNS(SVG_NS, "g") as SVGGElement;
      g.setAttribute("class", `edd-layer edd-layer-${name}`);
      this.world.appendChild(g);
      this.layers[name] = g;
    }

    this.screenLayer = doc.createElementNS(SVG_NS, "g") as SVGGElement;
    this.screenLayer.setAttribute("class", "edd-layer-screen");
    svg.appendChild(this.screenLayer);

    this.rc = rough.svg(svg);
    this.container.appendChild(svg);
    this.measure();
    this.applyCamera(this.camera);
  }

  destroy(): void {
    this.annotationLayerCache = null; // bound to the old <svg>
    this.svg?.remove();
  }

  private defsMarkup(): string {
    return `
      <linearGradient id="eddFlowGradient" x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
        <stop offset="0" stop-color="#4dabf7"/>
        <stop offset="0.4" stop-color="#22b8cf"/>
        <stop offset="0.7" stop-color="#845ef7"/>
        <stop offset="1" stop-color="#4dabf7"/>
      </linearGradient>
      <filter id="eddSoftGlow" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="2.2" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    `;
  }

  /** Read the container size; call on mount + resize. */
  measure(): { w: number; h: number } {
    // Layout pixels (clientWidth/Height), NOT getBoundingClientRect: the SVG
    // fills the container's LAYOUT box, so camera math must use the same
    // coordinate space. An embedder that scales the diagram with an ancestor
    // CSS transform (slide stages, thumbnails, zoomed previews) keeps
    // design-space-correct fits and paddings this way — gBCR would return
    // the visually scaled size and collapse focus paddings/zoom.
    const c = this.container;
    let w = c.clientWidth;
    let h = c.clientHeight;
    if (!w || !h) {
      const rect = c.getBoundingClientRect();
      w = w || rect.width;
      h = h || rect.height;
    }
    this.viewport = { w: Math.max(1, w), h: Math.max(1, h) };
    return this.viewport;
  }

  /**
   * State the viewport explicitly instead of measuring the container.
   *
   * `measure()` reads `clientWidth`/`clientHeight`, which a frame-driven host
   * often cannot rely on: Remotion mounts a composition inside a 0x0
   * off-screen wrapper during the layout pass, so a `measure()` call from a
   * `useLayoutEffect` sees 0x0 and pins the camera viewport at 1x1 — every
   * later `applyCamera` then translates by half a pixel and the diagram lands
   * in the top-left corner. Such a host knows the real size already
   * (Remotion's `useVideoConfig()`), so it should say so.
   *
   * Non-finite or non-positive dimensions are clamped to 1, exactly as
   * `measure()` clamps them. Re-applies the current camera so the world
   * transform matches the new viewport immediately.
   */
  setViewport(size: { w: number; h: number }): { w: number; h: number } {
    const w = Number.isFinite(size.w) ? size.w : 0;
    const h = Number.isFinite(size.h) ? size.h : 0;
    this.viewport = { w: Math.max(1, w), h: Math.max(1, h) };
    this.applyCamera(this.camera);
    return { ...this.viewport };
  }

  getViewportSize(): { w: number; h: number } {
    return { ...this.viewport };
  }

  getCamera(): CameraTransform {
    return { ...this.camera };
  }

  applyCamera(cam: CameraTransform): void {
    this.camera = cam;
    const { w, h } = this.viewport;
    const t = `translate(${w / 2} ${h / 2}) scale(${cam.zoom}) translate(${-cam.cx} ${-cam.cy})`;
    this.world.setAttribute("transform", t);
  }

  worldToScreen(p: Point): Point {
    const { w, h } = this.viewport;
    return {
      x: (p.x - this.camera.cx) * this.camera.zoom + w / 2,
      y: (p.y - this.camera.cy) * this.camera.zoom + h / 2,
    };
  }

  screenToWorld(p: Point): Point {
    const { w, h } = this.viewport;
    return {
      x: (p.x - w / 2) / this.camera.zoom + this.camera.cx,
      y: (p.y - h / 2) / this.camera.zoom + this.camera.cy,
    };
  }

  getLayer(name: "grid" | "groups" | "edges" | "nodes" | "annotations" | "live"): SVGGElement {
    return this.layers[name];
  }

  /** Show/hide nodes+edges for timeline reveal. `hidden` = ids to fade out. */
  applyVisibility(hidden: Set<string>): void {
    const els = this.svg.querySelectorAll<SVGGElement>("[data-node],[data-edge]");
    els.forEach((el) => {
      const id = el.getAttribute("data-node") ?? el.getAttribute("data-edge");
      if (id && hidden.has(id)) el.classList.add("edd-hidden");
      else el.classList.remove("edd-hidden");
    });
  }

  /**
   * Play a per-beat reveal animation (`fade` | `pop` | `sweep`) on the named
   * element groups. Clears any prior reveal classes first and forces a reflow so
   * re-entering the same beat restarts the animation. `fx` = id -> effect.
   */
  playReveal(fx: Record<string, string> | undefined): void {
    if (this.isStatic) return; // static frames must never catch a reveal mid-flight
    const classes = ["edd-reveal-fade", "edd-reveal-pop", "edd-reveal-sweep"];
    const els = this.svg.querySelectorAll<SVGGElement>("[data-node],[data-edge]");
    els.forEach((el) => el.classList.remove(...classes));
    if (!fx || !Object.keys(fx).length) return;
    // reflow so removed classes fully clear before we re-add (animation restart)
    void this.svg.getBoundingClientRect();
    els.forEach((el) => {
      const id = el.getAttribute("data-node") ?? el.getAttribute("data-edge");
      const cls = id ? REVEAL_EFFECT_CLASS[fx[id]] : undefined;
      if (cls) el.classList.add(cls);
    });
  }

  /**
   * Drive a hand-drawn "draw-on" reveal of one element from host code: strokes
   * sweep on in draw order (stroke-dashoffset), then labels fade in. `id` is a
   * node id, edge id, or viz-item key ("block.item" — all member elements sweep
   * as one continuous drawing). `progress` is 0..1; ≥1 restores the untouched
   * rendering (original dash patterns included), ≤0 hides the element. Built
   * for frame-driven hosts (Remotion, capture pipelines) — pair with
   * `{ static: true }` so no wall-clock CSS competes with it.
   */
  setRevealProgress(id: string, progress: number): void {
    const esc = cssEscape(id);
    const groups = this.svg.querySelectorAll<SVGGElement>(`[data-node="${esc}"],[data-edge="${esc}"],[data-viz-item="${esc}"]`);
    if (!groups.length) return;
    const p = Math.max(0, Math.min(1, progress));

    const strokes: SVGElement[] = [];
    const texts: SVGElement[] = [];
    groups.forEach((g) => {
      g.querySelectorAll<SVGElement>("path,line,polyline,polygon,circle,ellipse,rect").forEach((el) => strokes.push(el));
      g.querySelectorAll<SVGElement>("text").forEach((el) => texts.push(el));
    });

    // strokes complete at 85% progress; labels fade over the last 30%
    const sweep = Math.min(1, p / 0.85);
    const lens = strokes.map(strokeLength);
    const total = lens.reduce((a, b) => a + b, 0) || 1;
    let done = 0;
    strokes.forEach((el, i) => {
      const len = lens[i];
      const local = Math.max(0, Math.min(1, (sweep * total - done) / len));
      done += len;
      if (progress >= 1) {
        restoreStroke(el);
        return;
      }
      const hasStroke = (el.getAttribute("stroke") ?? "") !== "none";
      if (!hasStroke) {
        // fill-only element (solid rough fills): fade instead of dash-sweep
        el.style.opacity = String(local);
        return;
      }
      if (el.getAttribute("data-edd-dash") === null) {
        el.setAttribute("data-edd-dash", el.getAttribute("stroke-dasharray") ?? "");
      }
      el.setAttribute("stroke-dasharray", String(len));
      el.setAttribute("stroke-dashoffset", String(len * (1 - local)));
      el.style.opacity = local > 0 ? "" : "0";
    });

    const fade = Math.max(0, Math.min(1, (p - 0.7) / 0.3));
    texts.forEach((el) => {
      el.style.opacity = progress >= 1 ? "" : String(fade);
    });
  }

  /**
   * Batch form of {@link setRevealProgress} for frame-driven hosts, and the
   * ONLY safe one when frames are produced out of order (Remotion seeks, a
   * scrubber, a re-render worker).
   *
   * `setRevealProgress` MUTATES an element's dash pattern and only restores it
   * at `p >= 1`, so a frame that simply stops mentioning an id leaves that
   * element frozen mid-draw forever. This method takes the whole picture at
   * once: every drawable in the SVG that the map does NOT mention is restored
   * to `1` (fully drawn), so the DOM is always a total function of the map —
   * the same map always yields the same frame, whatever ran before it.
   *
   * Pass `0` to keep something un-drawn; omit an id to mean "finished".
   *
   *   renderer.setRevealProgressAll({ api: 0.4, db: 0 });   // everything else = done
   *
   * Ids are node ids, edge ids, and viz-item keys (`"block.item"`). Partial
   * progress wins over the implied restore when both address the same element
   * (a viz item and the nodes inside it), so nesting behaves as authored.
   */
  setRevealProgressAll(map: Record<string, number>): void {
    const ids = new Set<string>();
    this.svg.querySelectorAll<SVGElement>("[data-node],[data-edge],[data-viz-item]").forEach((el) => {
      for (const attr of ["data-node", "data-edge", "data-viz-item"]) {
        const id = el.getAttribute(attr);
        if (id) ids.add(id);
      }
    });
    for (const id of Object.keys(map)) ids.add(id);
    // Restore first, then apply the partials: a viz item and its member nodes
    // share elements, so the in-progress value must be written last.
    const partial: string[] = [];
    for (const id of ids) {
      const p = map[id];
      if (p == null || p >= 1) this.setRevealProgress(id, 1);
      else partial.push(id);
    }
    for (const id of partial) this.setRevealProgress(id, map[id]);
    // Restoring clears inline opacity with `style.opacity = ""`, which leaves an
    // EMPTY `style=""` attribute behind. It changes nothing on screen, but it
    // makes a restored element serialize differently from one that was never
    // touched — so exportSVGString/renderSceneToSVGString would stop being
    // byte-identical across seeks. Drop the empty leftovers so the serialized
    // frame is a total function of the map too, not just the painted one.
    this.svg.querySelectorAll('[style=""]').forEach((el) => el.removeAttribute("style"));
  }

  render(scene: Scene): void {
    this.scene = scene;
    // keyframes from runtime-registered arrow animations (cheap, idempotent)
    ensurePluginStyles(this.container.ownerDocument);
    // Paint the background on the mount container, not the SVG rect: the host's
    // dotted-grid background sits *behind* the SVG, so an opaque bg rect would
    // occlude it. Keeping the on-screen rect transparent lets the grid show
    // through. Export re-fills its own cloned rect (see export.ts), so exported
    // SVG/PNG still get a solid background.
    const bgColor = scene.meta.background || scene.theme.background;
    this.bg.setAttribute("fill", "transparent");
    this.container.style.backgroundColor = bgColor;

    // clear dynamic layers
    for (const name of ["groups", "edges", "nodes"]) this.layers[name].replaceChildren();

    // groups / container frames
    for (const group of scene.groups) {
      if (!group.frame) continue;
      const els = this.renderGroupFrame(scene, group);
      if (els) this.layers.groups.appendChild(els);
    }

    // edges (below nodes)
    const edges = [...scene.edges].sort((a, b) => a.z - b.z);
    for (const edge of edges) {
      try {
        const r = renderEdge(this.rc, scene, edge, { static: this.isStatic, ...this.roughTune() });
        if (edge.label) r.group.appendChild(this.edgeLabel(scene, edge, r.points));
        this.applyVizTags(r.group, edge.data);
        this.layers.edges.appendChild(r.group);
      } catch (err) {
        // never let one bad edge blank the whole diagram
        console.warn("edge render failed", edge.id, err);
      }
    }

    // nodes (above edges)
    const nodes = [...scene.nodes].sort((a, b) => a.z - b.z);
    for (const node of nodes) {
      try {
        this.layers.nodes.appendChild(this.renderNode(scene, node));
      } catch (err) {
        console.warn("node render failed", node.id, err);
      }
    }

    // always-on annotations (the top-level `annotate { … }` block). Rendering
    // them here makes `render(scene)` alone complete: a consumer that never
    // heard of AnnotationLayer still gets the marks the source asked for.
    // Step-scoped marks stay the timeline's job — TimelinePlayer/EdodoDraw
    // re-render this same layer with the step's set immediately after.
    if (this.paintsAnnotations) this.annotationLayer().render(scene, scene.annotations, false);
  }

  /** The scene the last `render()` painted (null before the first one). */
  getScene(): Scene | null {
    return this.scene;
  }

  private annotationLayerCache: AnnotationLayer | null = null;
  /** Lazily-built layer for the always-on annotations painted by render(). */
  private annotationLayer(): AnnotationLayer {
    return (this.annotationLayerCache ??= new AnnotationLayer(this));
  }

  /** Cache of gradient-fill defs created this mount: spec -> def id. */
  private gradientIds = new Map<string, string>();

  /**
   * Turn a `linear-gradient(#from,#to)` fill (emitted by gradient style
   * presets) into an SVG <linearGradient> def and return a url() reference.
   * Plain fills pass through untouched.
   */
  private resolveGradientFill(fill: string | null): string | null {
    if (!fill || !fill.startsWith("linear-gradient(")) return fill;
    const cached = this.gradientIds.get(fill);
    if (cached) return `url(#${cached})`;
    const inner = fill.slice("linear-gradient(".length, -1);
    const parts = inner.split(",").map((s) => s.trim());
    const [from, to] = parts.length >= 2 ? [parts[parts.length - 2], parts[parts.length - 1]] : [parts[0], parts[0]];
    const id = `eddGrad${this.gradientIds.size}`;
    const doc = this.container.ownerDocument;
    const grad = doc.createElementNS(SVG_NS, "linearGradient");
    grad.setAttribute("id", id);
    grad.setAttribute("x1", "0");
    grad.setAttribute("y1", "0");
    grad.setAttribute("x2", "0");
    grad.setAttribute("y2", "1");
    for (const [offset, color] of [
      ["0", from],
      ["1", to],
    ] as const) {
      const stop = doc.createElementNS(SVG_NS, "stop");
      stop.setAttribute("offset", offset);
      stop.setAttribute("stop-color", color);
      grad.appendChild(stop);
    }
    this.defs.appendChild(grad);
    this.gradientIds.set(fill, id);
    return `url(#${id})`;
  }

  /** Surface viz-item membership as DOM attributes so hosts can select one
   *  data item's elements as a unit: [data-viz-item="loads.intrinsic"]. */
  private applyVizTags(g: SVGGElement, data: Record<string, unknown> | undefined): void {
    const item = (data as { vizItem?: string } | undefined)?.vizItem;
    const role = (data as { vizRole?: string } | undefined)?.vizRole;
    if (item) g.setAttribute("data-viz-item", item);
    if (role) g.setAttribute("data-viz-role", role);
  }

  private renderNode(scene: Scene, node: SceneNode): SVGGElement {
    const doc = this.container.ownerDocument;
    const g = doc.createElementNS(SVG_NS, "g") as SVGGElement;
    g.setAttribute("data-node", node.id);
    g.setAttribute("class", "edd-node");
    this.applyVizTags(g, node.data);
    g.style.opacity = String(node.style.opacity / 100);

    if (node.shape === "character") {
      // Standalone sketchnote figure: the character library's strokes are
      // painted inside this node group; the label hangs under the feet,
      // inside the node box (so the bbox the layout computed is honoured).
      const paintText = (...args: Parameters<SvgRenderer["textBlock"]>) => this.textBlock(...args);
      const { body, labelCy, labelColor } = renderCharacterNode(this.rc, scene, node, doc, paintText, this.roughTune());
      g.appendChild(body);
      if (node.label) {
        g.appendChild(this.textBlock(node.label, node.x + node.w / 2, labelCy, node.style.fontSize, labelColor, node.style.fontFamily, "center", node.style.fontWeight));
      }
      return g;
    }
    if (node.shape === "icon") {
      // Glyph + caption: the sketchnote "icon + word" unit.
      const { body, labelCy, labelColor } = renderIconNode(this.rc, scene, node, doc, this.roughTune());
      g.appendChild(body);
      if (node.label) {
        g.appendChild(this.textBlock(node.label, node.x + node.w / 2, labelCy, node.style.fontSize, labelColor, node.style.fontFamily, "center", node.style.fontWeight));
      }
      return g;
    }

    const fill = this.resolveGradientFill(node.style.fill);
    const style = fill === node.style.fill ? node.style : { ...node.style, fill };
    const body = renderShapeBody(this.rc, node.shape, { x: node.x, y: node.y, w: node.w, h: node.h }, style, node.data, this.roughTune());
    g.appendChild(body);

    if (node.label) {
      const align = node.style.textAlign;
      const cx = align === "left" ? node.x : align === "right" ? node.x + node.w : node.x + node.w / 2;
      const below = labelBelow(node.shape);
      const lines = node.label.split("\n").length;
      const lineHeight = node.style.fontSize * 1.25;
      const blockHalf = ((lines - 1) * lineHeight) / 2;
      const vAlign = node.style.verticalAlign;
      let cy: number;
      if (below) cy = node.y + node.h + node.style.fontSize;
      else if (vAlign === "top") cy = node.y + node.style.fontSize * 0.75 + blockHalf;
      else if (vAlign === "bottom") cy = node.y + node.h - node.style.fontSize * 0.75 - blockHalf;
      else cy = node.y + node.h / 2;
      g.appendChild(this.textBlock(node.label, cx, cy, node.style.fontSize, node.style.textColor, node.style.fontFamily, align, node.style.fontWeight));
    }
    return g;
  }

  private textBlock(
    text: string,
    cx: number,
    cy: number,
    fontSize: number,
    color: string,
    family: SceneNode["style"]["fontFamily"],
    align: SceneNode["style"]["textAlign"] = "center",
    weight?: number,
  ): SVGTextElement {
    const doc = this.container.ownerDocument;
    const t = doc.createElementNS(SVG_NS, "text") as SVGTextElement;
    const lines = text.split("\n");
    const lineHeight = fontSize * 1.25;
    const anchor = align === "left" ? "start" : align === "right" ? "end" : "middle";
    t.setAttribute("text-anchor", anchor);
    // Named kinds map through FONT_FAMILY; presets may pass a raw CSS stack.
    t.setAttribute("font-family", FONT_FAMILY[family as keyof typeof FONT_FAMILY] ?? family);
    t.setAttribute("font-size", String(fontSize));
    if (weight) t.setAttribute("font-weight", String(weight));
    t.setAttribute("fill", color);
    t.setAttribute("dominant-baseline", "middle");
    t.style.whiteSpace = "pre";
    const startY = cy - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, i) => {
      const tspan = doc.createElementNS(SVG_NS, "tspan");
      tspan.setAttribute("x", String(cx));
      tspan.setAttribute("y", String(startY + i * lineHeight));
      tspan.textContent = line;
      t.appendChild(tspan);
    });
    return t;
  }

  private edgeLabel(scene: Scene, edge: (typeof scene.edges)[number], points: Point[]): SVGGElement {
    const doc = this.container.ownerDocument;
    const g = doc.createElementNS(SVG_NS, "g") as SVGGElement;
    // midpoint of the polyline
    const mid = points[Math.floor(points.length / 2)] ?? points[0];
    const fontSize = edge.style.fontSize;
    const padX = 6;
    const estW = edge.label.length * fontSize * 0.55 + padX * 2;
    if (edge.style.labelBg) {
      const rect = doc.createElementNS(SVG_NS, "rect");
      rect.setAttribute("x", String(mid.x - estW / 2));
      rect.setAttribute("y", String(mid.y - fontSize * 0.85));
      rect.setAttribute("width", String(estW));
      rect.setAttribute("height", String(fontSize * 1.7));
      rect.setAttribute("rx", "4");
      rect.setAttribute("fill", edge.style.labelBg);
      rect.setAttribute("opacity", "0.92");
      g.appendChild(rect);
    }
    g.appendChild(this.textBlock(edge.label, mid.x, mid.y, fontSize, edge.style.textColor, edge.style.fontFamily, "center"));
    return g;
  }

  private renderGroupFrame(scene: Scene, group: (typeof scene.groups)[number]): SVGGElement | null {
    const doc = this.container.ownerDocument;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const id of group.members) {
      const n = scene.nodes.find((x) => x.id === id);
      if (!n) continue;
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + n.w);
      maxY = Math.max(maxY, n.y + n.h);
    }
    if (!Number.isFinite(minX)) return null;
    const pad = 26;
    const g = doc.createElementNS(SVG_NS, "g") as SVGGElement;
    g.setAttribute("data-group", group.id);
    const stroke = group.style.stroke ?? "#adb5bd";
    // The frame has no per-element style of its own, so it follows the
    // diagram-wide declaration (`defaults { node { roughness … } }` or a preset
    // that carries rough tuning). Untouched — 0.8 / seed 42 — when nothing was
    // declared, which is every diagram written before this existed.
    const rectEl = this.rc.path(
      `M${minX - pad},${minY - pad} h${maxX - minX + pad * 2} v${maxY - minY + pad * 2} h${-(maxX - minX + pad * 2)} Z`,
      { ...sceneRoughOptions(scene, 0.8, this.roughnessScale), stroke, strokeWidth: 1.2, seed: 42, strokeLineDash: [6, 6], fill: group.style.fill ?? undefined, fillStyle: "solid" },
    );
    g.appendChild(rectEl);
    applyStrokeTuning(rectEl, this.roughTune());
    if (group.label) {
      g.appendChild(this.textBlock(group.label, minX - pad + 4, minY - pad - 2, 15, stroke, "hand", "left"));
    }
    return g;
  }
}

/** Roughness scales are multipliers: finite and non-negative. */
function clampScale(k: number): number {
  return Number.isFinite(k) && k > 0 ? k : 1;
}

// ---- reveal-progress helpers -------------------------------------------------

function cssEscape(s: string): string {
  const css = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS;
  if (css?.escape) return css.escape(s);
  return s.replace(/["\\]/g, "\\$&");
}

/** Path length of a drawable IN THE UNITS ITS DASHES ARE LAID OUT IN, with a
 *  safe fallback where getTotalLength is unavailable (jsdom) or throws
 *  (detached/degenerate geometry). getTotalLength() is in user units, but a
 *  `vector-effect: non-scaling-stroke` element dashes in viewport pixels —
 *  so under a zoomed camera (z > 1) a user-unit dash repeats mid-reveal
 *  (dash, gap, dash) and a circle closes only 1/z of the way. Scale by the
 *  CTM's area factor for those elements. */
function strokeLength(el: SVGElement): number {
  const geo = el as Partial<SVGGeometryElement>;
  if (typeof geo.getTotalLength === "function") {
    try {
      const len = geo.getTotalLength();
      if (Number.isFinite(len) && len > 0) return len * nonScalingFactor(el);
    } catch {
      /* fall through */
    }
  }
  return 120;
}

function nonScalingFactor(el: SVGElement): number {
  if (el.getAttribute("vector-effect") !== "non-scaling-stroke") return 1;
  const m = typeof (el as Partial<SVGGraphicsElement>).getCTM === "function" ? (el as SVGGraphicsElement).getCTM() : null;
  if (!m) return 1;
  const k = Math.sqrt(Math.abs(m.a * m.d - m.b * m.c));
  return Number.isFinite(k) && k > 0 ? k : 1;
}

function restoreStroke(el: SVGElement): void {
  const orig = el.getAttribute("data-edd-dash");
  if (orig !== null) {
    if (orig === "") el.removeAttribute("stroke-dasharray");
    else el.setAttribute("stroke-dasharray", orig);
    el.removeAttribute("data-edd-dash");
  }
  el.removeAttribute("stroke-dashoffset");
  el.style.opacity = "";
}
